// Domain events: what happened to a patient, in clinical time, from every source. Each event
// carries the FHIR R4 resources it creates or changes (CLAUDE.md L2.3 decisions). Pure.

import type { Resource } from '@medplum/fhirtypes';
import { z } from 'zod';

export const SCHEMA_VERSION = 1;

export const SOURCES = ['adt', 'lims', 'ris', 'pacs', 'vitals', 'epr', 'beds'] as const;
export type Source = (typeof SOURCES)[number];

/**
 * Who made an event: a source system, the platform's rules engine (L4.1), or a member of staff
 * acting on the board (L4.2, alert acknowledgement).
 */
export const EVENT_SOURCES = [...SOURCES, 'rules', 'staff'] as const;
export type EventSource = (typeof EVENT_SOURCES)[number];

/** ISO 8601 with an offset. Never a local time without offset. */
const instant = z.iso.datetime({ offset: true });

/**
 * Identifiers as the source sent them. Identity resolution (L2.4) turns them into a patient
 * and an encounter; until then nothing here is resolved.
 */
export const identifiersSchema = z
  .object({
    mrn: z.string().min(1),
    nhs_number: z.string().regex(/^\d{10}$/),
    visit_number: z.string().min(1),
    placer_order: z.string().min(1),
    filler_order: z.string().min(1),
    accession: z.string().min(1),
    study_uid: z.string().min(1),
    ambulance_id: z.string().min(1),
    /** The source system's own patient ID (for example an EPR Patient.id), linked in L2.4. */
    source_patient_ref: z.string().min(1),
    /** The source system's own encounter ID (for example an EPR Encounter.id), linked in L2.4. */
    source_encounter_ref: z.string().min(1),
  })
  .partial();
export type Identifiers = z.infer<typeof identifiersSchema>;

/** A data-quality check that failed. About the message, never about the patient. */
export const qualityIssueSchema = z.object({
  code: z.enum([
    'missing-event-time',
    'missing-nhs-number',
    'invalid-nhs-number',
    'unknown-location',
    'timestamp-without-offset',
    'missing-field',
    'unknown-code',
  ]),
  /** HL7 field (PV1-3) or JSON path. */
  field: z.string().optional(),
  detail: z.string(),
});
export type QualityIssue = z.infer<typeof qualityIssueSchema>;

export const LOCATION_KINDS = ['waiting', 'triage', 'zone', 'corridor', 'ward', 'other'] as const;

export const locationSchema = z.object({
  point_of_care: z.string(),
  room: z.string(),
  bed: z.string(),
  kind: z.enum(LOCATION_KINDS),
});
export type Location = z.infer<typeof locationSchema>;

const fhirResource = z.looseObject({ resourceType: z.string().min(1) });

const base = {
  id: z.uuid(),
  site_id: z.string().min(1),
  source: z.enum(EVENT_SOURCES),
  /** Clinical time: drives timers and ordering (CLAUDE.md hard rule 5). */
  occurred_at: instant,
  /** When the platform received the source message. Audit only. */
  recorded_at: instant,
  source_msg: z.object({ inbound_message_id: z.string().min(1), control_id: z.string().min(1) }),
  schema_ver: z.literal(SCHEMA_VERSION),
  /** Version of the mapper that made the event (MAPPER_VERSIONS). After a mapper fix, the
   *  reducer keeps only the newest version's events for each source message. */
  mapper_ver: z.number().int().positive(),
  identifiers: identifiersSchema,
  fhir: z.array(fhirResource),
  quality: z.array(qualityIssueSchema),
};

const event = <T extends string, D extends z.ZodType>(type: T, data: D) =>
  z.object({ ...base, type: z.literal(type), data });

const none = z.object({});
const atLocation = z.object({ location: locationSchema });

/** The presenting complaint as the PAS sent it (site code and text). Never interpreted. */
export const complaintSchema = z.object({
  text: z.string().min(1),
  code: z.string().min(1).optional(),
});
export type Complaint = z.infer<typeof complaintSchema>;

/** A triage category as the triage system sent it, e.g. Manchester Triage System 2. */
export const triageSchema = z.object({
  scale: z.string().min(1),
  category: z.number().int().min(1).max(5),
  label: z.string().min(1),
});
export type Triage = z.infer<typeof triageSchema>;
const labStatus = z.enum(['preliminary', 'final', 'corrected']);

export const ALERT_LEVELS = ['warning', 'breach'] as const;
export type AlertLevel = (typeof ALERT_LEVELS)[number];
const alertLevel = z.enum(ALERT_LEVELS);
const alertBase = {
  timer_id: z.string().min(1),
  /** Raise-and-clear round of this timer: an AlertCleared ends round `cycle`. */
  cycle: z.number().int().nonnegative(),
  /** Version of the site rules that made the event. */
  rules_version: z.number().int().positive(),
};

export const eventSchemas = {
  // ADT
  Arrived: event('Arrived', atLocation.extend({ complaint: complaintSchema.optional() })),
  Moved: event(
    'Moved',
    z.object({
      from: locationSchema.optional(),
      to: locationSchema,
      triage: triageSchema.optional(),
    }),
  ),
  Admitted: event('Admitted', atLocation),
  Discharged: event('Discharged', atLocation),
  DetailsUpdated: event('DetailsUpdated', none),
  ArrivalCancelled: event('ArrivalCancelled', none),
  DischargeCancelled: event('DischargeCancelled', atLocation),
  PatientsMerged: event('PatientsMerged', z.object({ prior_mrn: z.string().min(1) })),
  PatientsUnmerged: event('PatientsUnmerged', z.object({ prior_mrn: z.string().min(1) })),
  // Labs (L2.3b)
  LabOrdered: event('LabOrdered', z.object({ panel_code: z.string() })),
  LabSpecimenReceived: event('LabSpecimenReceived', z.object({ panel_code: z.string() })),
  LabOrderCancelled: event('LabOrderCancelled', z.object({ panel_code: z.string() })),
  LabResulted: event('LabResulted', z.object({ panel_code: z.string(), status: labStatus })),
  // Imaging (L2.3b, L2.3c)
  ImagingOrdered: event('ImagingOrdered', z.object({ exam_code: z.string() })),
  ImagingStarted: event('ImagingStarted', z.object({ exam_code: z.string() })),
  ImagingCompleted: event('ImagingCompleted', z.object({ exam_code: z.string() })),
  ImagingCancelled: event('ImagingCancelled', z.object({ exam_code: z.string() })),
  ImagingReported: event('ImagingReported', z.object({ exam_code: z.string(), status: labStatus })),
  ImagingStudyAvailable: event('ImagingStudyAvailable', z.object({ modality: z.string() })),
  // Vitals and EPR (L2.3c)
  VitalsRecorded: event('VitalsRecorded', z.object({ device_id: z.string() })),
  EncounterUpdated: event('EncounterUpdated', none),
  PatientHistoryRecorded: event('PatientHistoryRecorded', none),
  NoteRecorded: event('NoteRecorded', none),
  // Ambulance (L2.3c)
  AmbulanceArrived: event(
    'AmbulanceArrived',
    z.object({ priority: z.string(), pre_alert: z.boolean() }),
  ),
  AmbulanceHandedOver: event('AmbulanceHandedOver', none),
  // Rules engine (L4.1): a timer passed a fixed threshold, or stopped. Deterministic: a time
  // compared with a configured limit, never a score (hard rule 3).
  AlertRaised: event(
    'AlertRaised',
    z.object({ ...alertBase, level: alertLevel, started_at: instant, threshold_at: instant }),
  ),
  AlertCleared: event(
    'AlertCleared',
    z.object({
      ...alertBase,
      /** The level that was active. */
      level: alertLevel,
      reason: z.enum([
        'stopped',
        /** A late message stopped the timer before the alert's threshold. */
        'stopped-before-threshold',
        /** A corrected start time puts the timer back under the threshold. */
        'below-threshold',
        'no-start',
        'disabled',
      ]),
    }),
  ),
  // Escalation (L4.2). A member of staff with `alert:acknowledge` saw the alert at this level.
  // It stops further escalation of that level only; it never clears or hides the alert.
  AlertAcknowledged: event(
    'AlertAcknowledged',
    z.object({
      timer_id: z.string().min(1),
      cycle: z.number().int().nonnegative(),
      level: alertLevel,
      /** The identity provider's subject (`sub`) only: no name or email in an append-only store. */
      acknowledged_by: z.string().min(1),
    }),
  ),
  // The alert stayed without acknowledgement for a step's fixed number of minutes (site rules).
  // Recorded only: delivery to the people named comes with L5.3.
  AlertEscalated: event(
    'AlertEscalated',
    z.object({
      ...alertBase,
      level: alertLevel,
      /** 1 for the first step of the chain. */
      step: z.number().int().positive(),
      /** Who the step is for, as the site rules name them (e.g. "ED nurse in charge"). */
      notify: z.string().min(1),
      /** The alert's time plus the step's minutes. Also the event's occurred_at. */
      due_at: instant,
    }),
  ),
  // Corrections (L3.1): a source message that a newer mapper version maps to no events.
  SourceMessageRemapped: event('SourceMessageRemapped', none),
} as const;

export type EventType = keyof typeof eventSchemas;
export const EVENT_TYPES = Object.keys(eventSchemas) as EventType[];

export const domainEventSchema = z.discriminatedUnion(
  'type',
  Object.values(eventSchemas) as unknown as [
    (typeof eventSchemas)[EventType],
    ...(typeof eventSchemas)[EventType][],
  ],
);

type Inferred = { [K in EventType]: z.infer<(typeof eventSchemas)[K]> };

/** A domain event with its FHIR payload typed as FHIR resources. */
export type DomainEvent<K extends EventType = EventType> = Omit<Inferred[K], 'fhir'> & {
  fhir: Resource[];
};

/** Validates an event. Throws a ZodError listing every problem. */
export function parseEvent(value: unknown): DomainEvent {
  return domainEventSchema.parse(value) as DomainEvent;
}
